import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getAllProjects } from '../../services/projectService';
import { generateParentReport, generateDynamicProjectReport } from '../../services/reportService';
import { getAllMentors } from '../../services/mentorService';
import { getAllStudents } from '../../services/studentService';
import { downloadPdfDocument } from '../../utils/helpers';
import { useToast } from '../../context/ToastContext';
import styles from './Reports.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return [value];
};

const humanize = (value) => {
  if (!value) return 'Not specified';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
};

const getMarkdownSection = (markdown, heading) => {
  const pattern = new RegExp(`## ${heading}\\n([\\s\\S]*?)(?=\\n---|\\n## |$)`, 'i');
  return markdown.match(pattern)?.[1]?.trim() || '';
};

const getMarkdownField = (markdown, label) => {
  const pattern = new RegExp(`\\*\\*${label}:\\*\\*\\s*([^\\n]+)`, 'i');
  return markdown.match(pattern)?.[1]?.replace(/\s{2,}$/g, '').trim() || '';
};

const parseStaticParentReport = (markdown = '') => {
  const milestonesSection = getMarkdownSection(markdown, 'Milestones Progress');

  const milestones = [...milestonesSection.matchAll(/\*\*(?:.+?\s)?Milestone\s+([^:]+):\s*([^*]+)\*\*\s+Status:\s*([^\n]+)/g)]
    .map(match => ({
      number: match[1].trim(),
      name: match[2].trim(),
      status: match[3].trim()
    }));

  return {
    markdown,
    student: getMarkdownField(markdown, 'Student'),
    project: getMarkdownField(markdown, 'Project'),
    mentor: getMarkdownField(markdown, 'Mentor'),
    track: getMarkdownField(markdown, 'Track'),
    reportDate: getMarkdownField(markdown, 'Report Date'),
    executiveSummary: getMarkdownSection(markdown, 'Executive Summary'),
    milestones
  };
};

const normalizeDynamicReport = (reportPayload) => {
  if (!reportPayload) return null;

  if (typeof reportPayload === 'string') {
    try {
      return JSON.parse(reportPayload);
    } catch (error) {
      return {
        title: 'Project Progress Report',
        project_description: reportPayload,
        overall_status: 'insufficient_data',
        confidence: 'low'
      };
    }
  }

  return reportPayload;
};

const renderList = (items, emptyMessage = 'No details available.') => {
  const values = asArray(items);
  if (!values.length) return <p className={styles.muted}>{emptyMessage}</p>;

  return (
    <ul>
      {values.map((item, index) => (
        <li key={`${String(item).slice(0, 24)}-${index}`}>{item}</li>
      ))}
    </ul>
  );
};

const buildCombinedReportPdfDocument = (combinedReport) => {
  const staticReport = combinedReport.staticReport || {};
  const dynamicReport = combinedReport.dynamicReport;
  const progress = dynamicReport?.current_project_progress || {};
  const pastProgress = asArray(dynamicReport?.past_project_progress);
  const staticSections = [
    {
      title: 'Executive Summary',
      body: staticReport.executiveSummary || 'No executive summary available.'
    },
    {
      title: 'Milestones Progress',
      body: staticReport.milestones?.length ? '' : 'No milestones available.',
      subsections: staticReport.milestones?.map(milestone => ({
        title: `Milestone ${milestone.number}: ${milestone.name}`,
        body: `Status: ${milestone.status}`
      })) || []
    }
  ];

  const dynamicSections = dynamicReport ? [
    {
      title: 'Current Project Progress',
      body: progress.summary || 'No current progress summary available.',
      subsections: [
        { title: 'Evidence', items: asArray(progress.evidence) },
        { title: 'Risks or Blockers', items: asArray(progress.risks_or_blockers) },
        { title: 'Next Steps', items: asArray(progress.next_steps) }
      ]
    },
    {
      title: 'Past Project Progress',
      body: pastProgress.length ? '' : 'No past progress sessions available.',
      subsections: pastProgress.map((item, index) => ({
        title: `Session ${item.session_no || index + 1}`,
        body: item.summary || 'No session summary available.',
        items: asArray(item.progress_observed)
      }))
    },
    {
      title: 'Completion Outlook',
      body: `Expected completion: ${dynamicReport.expected_completion || 'Not specified'}`
    },
    {
      title: 'Mentor Remarks',
      body: dynamicReport.mentor_remarks || 'Not specified'
    }
  ] : [
    {
      title: 'Dynamic SYNC Progress',
      body: combinedReport.dynamicError || 'Dynamic SYNC report was not generated for this project.'
    }
  ];

  return {
    title: dynamicReport?.title || staticReport.project || 'Project Progress Report',
    subtitle: dynamicReport?.project_description || staticReport.executiveSummary || 'Project progress report.',
    meta: [
      { label: 'Student', value: dynamicReport?.student_name || staticReport.student },
      { label: 'Mentor', value: dynamicReport?.mentor_name || staticReport.mentor },
      { label: 'Track', value: staticReport.track },
      { label: 'Report date', value: staticReport.reportDate },
      { label: 'Status', value: dynamicReport ? humanize(dynamicReport.overall_status || 'insufficient_data') : 'Static only' },
      { label: 'Confidence', value: dynamicReport ? humanize(dynamicReport.confidence || 'low') : 'Not available' },
      { label: 'SYNC summaries', value: combinedReport.summaryCount ?? 0 },
      { label: 'Sessions completed', value: dynamicReport?.sessions_completed ?? 'Not specified' }
    ],
    sections: [...staticSections, ...dynamicSections]
  };
};

const Reports = () => {
  const [allProjects, setAllProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [combinedReport, setCombinedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const toast = useToast();
  const dynamicReportContent = useMemo(
    () => normalizeDynamicReport(combinedReport?.dynamicPayload?.report),
    [combinedReport]
  );

  const filterProjects = useCallback(() => {
    let filtered = [...allProjects];

    if (selectedMentor) {
      filtered = filtered.filter(p => p.mentor_id === selectedMentor);
    }

    if (selectedStudent) {
      filtered = filtered.filter(p => p.student_id === selectedStudent);
    }

    setFilteredProjects(filtered);
    
    if (selectedProject && !filtered.find(p => p.id === selectedProject)) {
      setSelectedProject('');
      setCombinedReport(null);
    }
  }, [allProjects, selectedMentor, selectedStudent, selectedProject]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [filterProjects]);

  const fetchData = async () => {
    try {
      const [projectsData, mentorsData, studentsData] = await Promise.all([
        getAllProjects(),
        getAllMentors(),
        getAllStudents()
      ]);
      setAllProjects(projectsData);
      setFilteredProjects(projectsData);
      setMentors(mentorsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedProject) {
      toast.warning('Please select a project');
      return;
    }

    const project = allProjects.find(p => p.id === selectedProject);
    setGeneratingReport(true);
    try {
      const parentReportPromise = generateParentReport(selectedProject);
      const dynamicReportPromise = project?.sync_group_id
        ? generateDynamicProjectReport(selectedProject)
        : Promise.resolve(null);

      const [parentResult, dynamicResult] = await Promise.allSettled([
        parentReportPromise,
        dynamicReportPromise
      ]);

      if (parentResult.status === 'rejected') {
        throw parentResult.reason;
      }

      const dynamicPayload = dynamicResult.status === 'fulfilled' ? dynamicResult.value : null;
      const dynamicError = dynamicResult.status === 'rejected'
        ? (dynamicResult.reason?.response?.data?.detail || dynamicResult.reason?.message || 'Dynamic report could not be generated.')
        : project?.sync_group_id ? '' : 'This project is not linked with SYNC, so only the static report is shown.';

      setCombinedReport({
        staticReport: parseStaticParentReport(parentResult.value.report),
        dynamicPayload,
        dynamicError,
        summaryCount: dynamicPayload?.summary_count ?? 0
      });

      if (dynamicError) {
        toast.warning(dynamicError);
      } else {
        toast.success('Report generated successfully.');
      }
    } catch (error) {
      toast.error('Error generating report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingReport(false);
    }
  };

  const handlePdfDownload = () => {
    if (!combinedReport) return;

    const projectName = allProjects.find(p => p.id === selectedProject)?.project_name || 'project';
    const safeProjectName = projectName.replace(/[^a-z0-9_-]+/gi, '_').replace(/^_+|_+$/g, '') || 'project';

    try {
      downloadPdfDocument(
        buildCombinedReportPdfDocument({
          ...combinedReport,
          dynamicReport: dynamicReportContent
        }),
        `${safeProjectName}_progress_report`
      );
      toast.success('PDF report downloaded.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    setCombinedReport(null);
  };

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.reportsPage}>
      <h1>Parent Reports</h1>

      <Card>
        <div className={styles.controls}>
          <div className={styles.formGroup}>
            <label>Select Mentor</label>
            <select value={selectedMentor} onChange={(e) => setSelectedMentor(e.target.value)} className={styles.select}>
              <option value="">-- Select --</option>
              {mentors.map(mentor => (
                <option key={mentor.id} value={mentor.id}>
                  {mentor.first_name} {mentor.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Select Student</label>
            <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} className={styles.select}>
              <option value="">-- Select --</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.first_name} {student.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Select Project</label>
            <select value={selectedProject} onChange={(e) => handleProjectChange(e.target.value)} className={styles.select}>
              <option value="">-- Select --</option>
              {sortProjectsByName(filteredProjects).map(project => (
                <option key={project.id} value={project.id}>
                  {project.project_name}{project.sync_group_id ? ' (SYNC)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <Button
            variant="secondary"
            onClick={handleGenerateReport}
            loading={generatingReport}
            disabled={!selectedProject || generatingReport}
          >
            {generatingReport ? 'Generating...' : 'Generate Report'}
          </Button>
        </div>
      </Card>

      {combinedReport && (
        <Card
          title="Generated Progress Report"
          action={
            <Button variant="outline" size="small" onClick={handlePdfDownload}>
              Download PDF
            </Button>
          }
        >
          <div className={styles.dynamicReport}>
            <div className={styles.reportMeta}>
              <span>Student: {dynamicReportContent?.student_name || combinedReport.staticReport.student || 'Not specified'}</span>
              <span>Mentor: {dynamicReportContent?.mentor_name || combinedReport.staticReport.mentor || 'Not specified'}</span>
              <span>Track: {combinedReport.staticReport.track || 'Not specified'}</span>
              <span>Status: {dynamicReportContent ? humanize(dynamicReportContent.overall_status || 'insufficient_data') : 'Static only'}</span>
              <span>SYNC summaries: {combinedReport.summaryCount}</span>
              <span>Sessions completed: {dynamicReportContent?.sessions_completed ?? 'Not specified'}</span>
            </div>

            <section className={styles.reportSection}>
              <h2>{dynamicReportContent?.title || combinedReport.staticReport.project || 'Project Progress Report'}</h2>
              <div className={styles.reportSummaryGrid}>
                <div>
                  <span>Expected Completion</span>
                  <strong>{dynamicReportContent?.expected_completion || 'Not specified'}</strong>
                </div>
              </div>
              <p>{dynamicReportContent?.project_description || combinedReport.staticReport.executiveSummary || 'No project description available.'}</p>
            </section>

            <section className={styles.reportSection}>
              <h3>Executive Summary</h3>
              <p>{combinedReport.staticReport.executiveSummary || 'No executive summary available.'}</p>
            </section>

            <section className={styles.reportSection}>
              <h3>Milestones Progress</h3>
              {combinedReport.staticReport.milestones?.length ? (
                <div className={styles.timeline}>
                  {combinedReport.staticReport.milestones.map((milestone, index) => (
                    <div key={`milestone-${index}`} className={styles.timelineItem}>
                      <div className={styles.timelineDate}>Milestone {milestone.number}</div>
                      <p><strong>{milestone.name}</strong></p>
                      <p>Status: {milestone.status}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No milestones available.</p>
              )}
            </section>

            {dynamicReportContent ? (
              <>
                <section className={styles.reportSection}>
                  <h3>Current SYNC Progress</h3>
                  <p>{dynamicReportContent.current_project_progress?.summary || 'No current progress summary available.'}</p>

                  <h4>Evidence</h4>
                  {renderList(dynamicReportContent.current_project_progress?.evidence)}

                  <h4>Risks or Blockers</h4>
                  {renderList(dynamicReportContent.current_project_progress?.risks_or_blockers)}

                  <h4>Next Steps</h4>
                  {renderList(dynamicReportContent.current_project_progress?.next_steps)}
                </section>

                <section className={styles.reportSection}>
                  <h3>Past SYNC Progress</h3>
                  {asArray(dynamicReportContent.past_project_progress).length ? (
                    <div className={styles.timeline}>
                      {asArray(dynamicReportContent.past_project_progress).map((item, index) => (
                        <div key={`past-${index}`} className={styles.timelineItem}>
                          <div className={styles.timelineDate}>Session {item.session_no || index + 1}</div>
                          <p>{item.summary || 'No session summary available.'}</p>
                          {renderList(item.progress_observed)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No past SYNC meeting summaries were available before the latest meeting.</p>
                  )}
                </section>

                <section className={styles.reportSection}>
                  <h3>Mentor Remarks</h3>
                  <p>{dynamicReportContent.mentor_remarks || 'Not specified'}</p>
                </section>
              </>
            ) : (
              <section className={styles.reportSection}>
                <h3>Dynamic SYNC Progress</h3>
                <p>{combinedReport.dynamicError || 'Dynamic SYNC report was not generated for this project.'}</p>
              </section>
            )}

            {dynamicReportContent?.current_project_progress?.next_steps?.length ? (
              <section className={styles.reportSection}>
                <h3>Next Steps</h3>
                {renderList(dynamicReportContent.current_project_progress.next_steps)}
              </section>
            ) : null}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Reports;
